import { User, UserRole, USER_LIMITS } from './types';

// Storage keys
const USER_STORAGE_KEY = 'current_user';
const OFFERS_COUNT_KEY = 'offers_generated_count';
const SAVED_POSTS_KEY = 'saved_posts';
const USERS_STORAGE_KEY = 'users';
const LINKEDIN_TOKEN_KEY = 'linkedin_token';
const LINKEDIN_TOKEN_EXPIRY_KEY = 'linkedin_token_expiry';

/**
 * Save users to storage
 */
const saveUsers = (users: User[]): void => {
  try {
    console.log('Saving users to storage:', users);
    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
    
    // Dispatch storage event for cross-tab synchronization
    window.dispatchEvent(new StorageEvent('storage', {
      key: USERS_STORAGE_KEY,
      newValue: JSON.stringify(users)
    }));
  } catch (error) {
    console.error('Error saving users:', error);
  }
};

// Demo users with passwords
const DEMO_USERS: User[] = [
  {
    id: 'admin-1',
    email: 'admin@example.com',
    name: 'Admin',
    role: 'admin',
    createdAt: new Date().toISOString(),
    remainingCredits: Infinity,
    password: 'password123'
  },
  {
    id: 'pro-1',
    email: 'pro@example.com',
    name: 'Client Pro',
    role: 'pro',
    createdAt: new Date().toISOString(),
    remainingCredits: USER_LIMITS.pro.maxPosts,
    subscriptionEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    subscriptionAmount: '19.90',
    subscriptionDate: new Date().toISOString(),
    nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    password: 'password123'
  },
  {
    id: 'trial-1',
    email: 'trial@example.com',
    name: 'Utilisateur Essai',
    role: 'trial',
    createdAt: new Date().toISOString(),
    remainingCredits: USER_LIMITS.trial.maxPosts,
    password: 'password123'
  }
];

// Initialize users in localStorage immediately
saveUsers(DEMO_USERS);

/**
 * Get all users from storage
 */
const getAllUsers = (): User[] => {
  try {
    console.log('Getting all users from storage');
    const usersJson = localStorage.getItem(USERS_STORAGE_KEY);
    if (usersJson) {
      const users = JSON.parse(usersJson);
      console.log('Users found in storage:', users);
      return users;
    }
    
    // Initialize with demo users if no users exist
    console.log('No users found, initializing with demo users');
    saveUsers(DEMO_USERS);
    return DEMO_USERS;
  } catch (error) {
    console.error('Error getting users:', error);
    return DEMO_USERS;
  }
};

/**
 * Create a new user
 */
export const createUser = (email: string, password: string, name: string): User => {
  console.log('Creating new user:', { email, name });
  const users = getAllUsers();
  
  // Check if user already exists
  if (users.find(u => u.email.toLowerCase() === email.toLowerCase())) {
    console.error('User already exists:', email);
    throw new Error('Un utilisateur avec cet email existe déjà');
  }

  // Create new user with trial role
  const newUser: User = {
    id: `user-${Date.now()}`,
    email: email.toLowerCase().trim(),
    name,
    password,
    role: 'trial',
    remainingCredits: USER_LIMITS.trial.maxPosts,
    createdAt: new Date().toISOString()
  };

  // Save user to storage
  const updatedUsers = [...users, newUser];
  saveUsers(updatedUsers);

  // Set as current user
  console.log('Setting current user:', newUser);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(newUser));
  
  // Dispatch storage event
  window.dispatchEvent(new StorageEvent('storage', {
    key: USER_STORAGE_KEY,
    newValue: JSON.stringify(newUser)
  }));

  return newUser;
};

/**
 * Login user
 */
export const loginUser = async (email: string, password: string): Promise<User> => {
  console.log('Attempting login:', email);
  
  // Get users, ensuring demo users are initialized
  const users = getAllUsers();
  
  // Normalize email for comparison
  const normalizedEmail = email.toLowerCase().trim();
  
  console.log('Looking for user with email:', normalizedEmail);
  console.log('Available users:', users);
  
  const user = users.find(u => 
    u.email.toLowerCase() === normalizedEmail && 
    (u.password === password || password === 'linkedin-auth')
  );

  if (!user) {
    console.error('Login failed: Invalid credentials');
    console.error('Attempted email:', normalizedEmail);
    console.error('Attempted password:', password);
    throw new Error('Email ou mot de passe incorrect');
  }

  const updatedUser = {
    ...user,
    lastLogin: new Date().toISOString()
  };

  // Update user's last login
  const updatedUsers = users.map(u => u.id === user.id ? updatedUser : u);
  saveUsers(updatedUsers);

  // Set current user
  console.log('Setting current user after login:', updatedUser);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));
  
  // Dispatch storage event
  window.dispatchEvent(new StorageEvent('storage', {
    key: USER_STORAGE_KEY,
    newValue: JSON.stringify(updatedUser)
  }));

  return updatedUser;
};

/**
 * Get the current user
 */
export const getCurrentUser = (): User | null => {
  try {
    console.log('Getting current user from storage');
    const userJson = localStorage.getItem(USER_STORAGE_KEY);
    if (userJson) {
      const user = JSON.parse(userJson);
      console.log('Current user found:', user);
      return user;
    }
    console.log('No current user found');
    return null;
  } catch (error) {
    console.error('Error retrieving user:', error);
    return null;
  }
};

/**
 * Logout user
 */
export const logoutUser = (): void => {
  console.log('Logging out user');
  localStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem(LINKEDIN_TOKEN_KEY);
  localStorage.removeItem(LINKEDIN_TOKEN_EXPIRY_KEY);
  
  const monthKey = getCurrentMonthKey();
  localStorage.removeItem(`${OFFERS_COUNT_KEY}_${monthKey}`);
  
  // Dispatch storage event
  window.dispatchEvent(new StorageEvent('storage', {
    key: USER_STORAGE_KEY,
    oldValue: localStorage.getItem(USER_STORAGE_KEY),
    newValue: null
  }));
  
  console.log('User logged out successfully');
};

/**
 * Check if a user has admin role
 */
export const isAdmin = (user: User | null): boolean => {
  return user?.role === 'admin';
};

/**
 * Check if user has pro access
 */
export const hasProAccess = (user: User | null): boolean => {
  if (!user) return false;
  return ['admin', 'pro', 'entreprise'].includes(user.role);
};

/**
 * Get the current month's key for storing offer count
 */
const getCurrentMonthKey = () => {
  const date = new Date();
  return `${date.getFullYear()}-${date.getMonth() + 1}`;
};

/**
 * Get the number of offers generated this month
 */
export const getOffersGeneratedThisMonth = (): number => {
  const monthKey = getCurrentMonthKey();
  const count = localStorage.getItem(`${OFFERS_COUNT_KEY}_${monthKey}`);
  return count ? parseInt(count, 10) : 0;
};

/**
 * Increment the number of offers generated this month
 */
export const incrementOffersGenerated = (): void => {
  const monthKey = getCurrentMonthKey();
  const currentCount = getOffersGeneratedThisMonth();
  localStorage.setItem(`${OFFERS_COUNT_KEY}_${monthKey}`, (currentCount + 1).toString());
};

/**
 * Check if a user has remaining credits
 */
export const hasRemainingCredits = (): boolean => {
  const user = getCurrentUser();
  if (!user) return false;

  if (isAdmin(user)) return true;

  const limits = USER_LIMITS[user.role];
  if (!limits) return false;

  const offersGenerated = getOffersGeneratedThisMonth();
  const maxOffers = limits.maxPosts;
  
  return maxOffers === 'Illimité' || offersGenerated < maxOffers;
};

/**
 * Decrement credits (increment offers generated)
 */
export const decrementCredits = (): void => {
  const user = getCurrentUser();
  if (!user) {
    throw new Error("Utilisateur non connecté");
  }

  if (isAdmin(user)) return;

  if (!hasRemainingCredits()) {
    throw new Error("Vous avez atteint votre limite d'offres pour ce mois");
  }
  
  incrementOffersGenerated();
};

/**
 * Get subscription info
 */
export const getSubscriptionInfo = () => {
  const user = getCurrentUser();
  if (!user) return null;

  const limits = USER_LIMITS[user.role];
  if (!limits) return null;

  const offersGenerated = getOffersGeneratedThisMonth();
  const maxOffers = limits.maxPosts;
  const remainingOffers = maxOffers === 'Illimité' ? 'Illimité' : Math.max(0, maxOffers - offersGenerated);
  
  return {
    type: limits.name,
    status: 'Actif',
    expiresAt: user.nextBillingDate,
    remainingCredits: remainingOffers,
    offersGenerated,
    maxOffers,
    amount: user.subscriptionAmount,
    nextBillingDate: user.nextBillingDate,
    subscriptionDate: user.subscriptionDate
  };
};

/**
 * Check if premium subscription is valid
 */
export const isPremiumValid = (): boolean => {
  const user = getCurrentUser();
  if (!user || !['pro', 'entreprise'].includes(user.role)) return false;
  
  if (!user.nextBillingDate) return false;
  
  const nextBilling = new Date(user.nextBillingDate);
  return nextBilling > new Date();
};

/**
 * Get remaining offers for the current month
 */
export const getRemainingOffers = (): number | 'Illimité' => {
  const user = getCurrentUser();
  if (!user) return 0;
  
  const limits = USER_LIMITS[user.role];
  if (!limits) return 0;
  
  const maxOffers = limits.maxPosts;
  
  if (maxOffers === 'Illimité') return 'Illimité';
  
  const offersGenerated = getOffersGeneratedThisMonth();
  return Math.max(0, maxOffers - offersGenerated);
};

/**
 * Save a job post
 */
export const saveJobPost = async (post: any): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    // Get existing saved posts
    const savedPosts = JSON.parse(localStorage.getItem(SAVED_POSTS_KEY) || '[]');
    
    // Add new post with user ID
    const updatedPosts = [...savedPosts, { ...post, userId: user.id }];
    
    // Save back to localStorage
    localStorage.setItem(SAVED_POSTS_KEY, JSON.stringify(updatedPosts));
  } catch (error) {
    console.error('Error saving post:', error);
    throw new Error('Erreur lors de la sauvegarde de l\'offre');
  }
};

/**
 * Get saved job posts for current user
 */
export const getSavedPosts = (): any[] => {
  const user = getCurrentUser();
  if (!user) return [];
  
  try {
    const savedPosts = JSON.parse(localStorage.getItem(SAVED_POSTS_KEY) || '[]');
    return savedPosts.filter((post: any) => post.userId === user.id);
  } catch (error) {
    console.error('Error getting saved posts:', error);
    return [];
  }
};

/**
 * Update user subscription
 */
export const updateUserSubscription = (userId: string, subscriptionData: {
  role: UserRole;
  subscriptionAmount: string;
  subscriptionDate?: string;
  nextBillingDate?: string;
}): User | null => {
  const users = getAllUsers();
  const userIndex = users.findIndex(u => u.id === userId);
  
  if (userIndex === -1) return null;

  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());

  const updatedUser = {
    ...users[userIndex],
    role: subscriptionData.role,
    subscriptionAmount: subscriptionData.subscriptionAmount,
    subscriptionDate: subscriptionData.subscriptionDate || now.toISOString(),
    nextBillingDate: subscriptionData.nextBillingDate || nextMonth.toISOString(),
    remainingCredits: USER_LIMITS[subscriptionData.role].maxPosts
  };

  users[userIndex] = updatedUser;
  saveUsers(users);

  // Update current user if it's the same user
  const currentUser = getCurrentUser();
  if (currentUser && currentUser.id === userId) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));
    
    // Dispatch storage event
    window.dispatchEvent(new StorageEvent('storage', {
      key: USER_STORAGE_KEY,
      newValue: JSON.stringify(updatedUser)
    }));
  }

  return updatedUser;
}